import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import liff from '@line/liff';
import LiffService from '../../services/LiffService';
import LiffContext from './context/LiffContext';
import AwaitingActionPage from './pages/AwaitingActionPage';
import LiffOrderSummary from './pages/LiffOrderSummary';
import LiffPaymentPage from './pages/LiffPaymentPage';
import LiffSelectActionPage from './pages/LiffSelectActionPage';
import LiffLoading from './components/LiffLoading';
import { getLiffIdForShop, getShopFromHostname } from './config/constants';

const IS_DEV = process.env.NODE_ENV === 'development';

const STORAGE_KEY = 'liff_nav_data';
/** When set, user previously chose to see dashboard (e.g. Back from payment or refreshed on /liff); skip auto-redirect to payment so refresh keeps them on dashboard. */
const STORAGE_KEY_SAW_DASHBOARD = 'liff_saw_dashboard';

const saveNavData = (data) => {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch (_e) { /* */ }
};

const loadNavData = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_e) { return null; }
};

const LiffApp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState('loading');
  const [error, setError] = useState('');
  const [errorDetail, setErrorDetail] = useState('');
  const [customer, setCustomer] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [shopId, setShopId] = useState('');

  const [selectActionItems, setSelectActionItems] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [orderSummaryOrderIds, setOrderSummaryOrderIds] = useState([]);
  const [orderSummaryOrderIdsByGroup, setOrderSummaryOrderIdsByGroup] = useState(null);
  /** When user presses Back from payment, show dashboard without auto-redirecting back to payment */
  const [skipAutoRedirectToPayment, setSkipAutoRedirectToPayment] = useState(false);

  const shopParam = shopId ? `?shop=${shopId}` : '';

  const handleViewOrderSummary = (itemsOrOrderIds, orderIdsByGroup = null) => {
    const ids = Array.isArray(itemsOrOrderIds)
      ? itemsOrOrderIds.map((x) => (typeof x === 'object' && x != null && 'id' in x ? x.id : x)).filter(Boolean)
      : [];
    setOrderSummaryOrderIds(ids);
    setOrderSummaryOrderIdsByGroup(orderIdsByGroup || null);
    saveNavData({ orderSummaryOrderIds: ids, orderSummaryOrderIdsByGroup: orderIdsByGroup || null });
    navigate(`/liff/order-summary${shopParam}`);
  };

  const handleBackFromOrderSummary = () => {
    setSkipAutoRedirectToPayment(true);
    try {
      sessionStorage.setItem(STORAGE_KEY_SAW_DASHBOARD, '1');
    } catch (_e) { /* */ }
    setOrderSummaryOrderIds([]);
    setOrderSummaryOrderIdsByGroup(null);
    navigate(`/liff${shopParam}`);
  };

  const handleViewSelectAction = (items, paymentStatusFromData, options = {}) => {
    const itemsList = Array.isArray(items) ? items : [];
    setSelectActionItems(itemsList);

    const status = paymentStatusFromData || { requiresPayment: false };
    setPaymentStatus(status);

    saveNavData({ selectActionItems: itemsList, paymentStatus: status });

    if (status.requiresPayment) {
      // If customer closed after paying group1, send them to payment with ?after=group1 so they see "ยอดที่ 1 ชำระแล้ว" + group2
      const paymentQuery = options.group2Only
        ? (shopParam ? `${shopParam}&after=group1` : '?after=group1')
        : shopParam;
      navigate(`/liff/payment${paymentQuery}`);
    } else {
      navigate(`/liff/select-action${shopParam}`);
    }
  };

  const handlePaymentComplete = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY_SAW_DASHBOARD);
    } catch (_e) { /* */ }
    const saved = loadNavData();
    if (saved?.selectActionItems) {
      saveNavData({ selectActionItems: saved.selectActionItems });
    }
    navigate(`/liff/select-action${shopParam}`, { replace: true });
  };

  const handleBackFromPayment = () => {
    setSkipAutoRedirectToPayment(true);
    try {
      sessionStorage.setItem(STORAGE_KEY_SAW_DASHBOARD, '1');
    } catch (_e) { /* */ }
    setSelectActionItems([]);
    setPaymentStatus(null);
    navigate(`/liff${shopParam}`);
  };

  const handleBackFromSelectAction = () => {
    setSelectActionItems([]);
    setPaymentStatus(null);
    navigate(`/liff${shopParam}`);
  };

  const initLiff = useCallback(async () => {
    try {
      const shop = searchParams.get('shop') || getShopFromHostname();
      if (!shop) {
        const host = typeof window !== 'undefined' ? window.location.hostname : '';
        console.warn('[LIFF] No shop: missing ?shop= param and hostname not in HOST_TO_SHOP.', {
          hostname: host,
          searchParams: Object.fromEntries(searchParams.entries()),
          recommendation: 'Use ?shop=smile in the URL, or add this host to HOST_TO_SHOP in liff/config/constants.js and redeploy.',
        });
        setError('ลิงก์ไม่ถูกต้อง กรุณาใช้ลิงก์ที่ได้รับจากทางร้าน');
        setStep('error');
        return;
      }
      setShopId(shop);

      if (IS_DEV) {
        console.log('[LIFF DEV MODE] Skipping LIFF init, shop:', shop);
        setDisplayName('Dev User');
        const response = await LiffService.checkIdentity('dev-token', shop);
        const { linked, customer: customerData, displayName: name } = response.data;
        setDisplayName(name || 'Dev User');
        if (linked && customerData) {
          setCustomer(customerData);
          setAccessToken('dev-token');
          setStep('dashboard');
          // If user previously saw dashboard (Back from payment or refreshed on /liff), don't auto-redirect to payment
          try {
            if (sessionStorage.getItem(STORAGE_KEY_SAW_DASHBOARD) === '1') {
              setSkipAutoRedirectToPayment(true);
            }
          } catch (_e) { /* */ }
        } else {
          setStep('not-found');
        }
        return;
      }

      const liffId = getLiffIdForShop(shop);
      await liff.init({ liffId });

      if (!liff.isLoggedIn()) {
        liff.login();
        return;
      }

      const token = liff.getAccessToken();
      if (!token) {
        setError('ไม่สามารถรับ Access Token ได้ กรุณาลองใหม่อีกครั้ง');
        setStep('error');
        return;
      }

      const response = await LiffService.checkIdentity(token, shop);
      const { linked, customer: customerData, displayName: name } = response.data;

      setDisplayName(name || '');

      if (linked && customerData) {
        setCustomer(customerData);
        setAccessToken(token);
        setStep('dashboard');
        try {
          if (sessionStorage.getItem(STORAGE_KEY_SAW_DASHBOARD) === '1') {
            setSkipAutoRedirectToPayment(true);
          }
        } catch (_e) { /* */ }
      } else {
        setStep('not-found');
      }
    } catch (err) {
      const apiMessage = err.response?.data?.message;
      const apiDetail = err.response?.data?.detail;
      const status = err.response?.status;
      const fallback = err.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง';
      const displayMessage = apiMessage || fallback;

      console.error('[LIFF] init error:', {
        message: displayMessage,
        detail: apiDetail,
        status,
        shop: getShopFromHostname(),
        responseData: err.response?.data,
        errMessage: err.message,
      });

      setError(displayMessage + (apiDetail ? ` (${apiDetail})` : ''));
      setErrorDetail(
        [status && `HTTP ${status}`, apiDetail, err.response?.data ? JSON.stringify(err.response.data) : null]
          .filter(Boolean)
          .join(' · ')
      );
      setStep('error');
    }
  }, [searchParams]);

  useEffect(() => {
    initLiff();
  }, [initLiff]);

  if (step === 'loading') {
    return <LiffLoading />;
  }

  if (step === 'not-found') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">ไม่พบข้อมูลลูกค้า</h2>
          <p className="text-gray-500 text-sm mb-6">
            ไม่พบบัญชี LINE ของคุณในระบบ กรุณาติดต่อเจ้าหน้าที่เพื่อเชื่อมต่อบัญชีของคุณ
          </p>
          {displayName && (
            <p className="text-xs text-gray-400 mb-4">LINE: {displayName}</p>
          )}
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-green-500 text-white py-3 rounded-xl font-medium hover:bg-green-600 transition-colors"
          >
            ลองใหม่อีกครั้ง
          </button>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">เกิดข้อผิดพลาด</h2>
          <p className="text-gray-500 text-sm mb-4">{error}</p>
          {errorDetail && (
            <p className="text-left text-xs text-gray-400 bg-gray-100 rounded-lg p-3 mb-4 font-mono break-all" title="Debug info">
              {errorDetail}
            </p>
          )}
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-green-500 text-white py-3 rounded-xl font-medium hover:bg-green-600 transition-colors"
          >
            ลองใหม่อีกครั้ง
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {step === 'dashboard' && (
        <LiffContext.Provider value={{ accessToken, shopId, customer, displayName }}>
          <Routes>
            <Route
              index
              element={
                <AwaitingActionPage
                  skipAutoRedirectToPayment={skipAutoRedirectToPayment}
                  onViewOrderSummary={handleViewOrderSummary}
                  onViewSelectAction={handleViewSelectAction}
                />
              }
            />
            <Route
              path="order-summary"
              element={
                <RestoreOrRedirect
                  restoreKey="orderSummaryOrderIds"
                  onRestore={(saved) => {
                    setOrderSummaryOrderIds(saved.orderSummaryOrderIds || []);
                    setOrderSummaryOrderIdsByGroup(saved.orderSummaryOrderIdsByGroup || null);
                  }}
                  hasData={orderSummaryOrderIds.length > 0}
                  shopParam={shopParam}
                >
                  <LiffOrderSummary
                    orderIds={orderSummaryOrderIds}
                    orderIdsByGroup={orderSummaryOrderIdsByGroup}
                    onBack={handleBackFromOrderSummary}
                  />
                </RestoreOrRedirect>
              }
            />
            <Route
              path="payment"
              element={
                <RestoreOrRedirect
                  restoreKey="paymentStatus"
                  onRestore={(saved) => {
                    setSelectActionItems(saved.selectActionItems || []);
                    setPaymentStatus(saved.paymentStatus || null);
                  }}
                  hasData={!!paymentStatus}
                  shopParam={shopParam}
                >
                  <LiffPaymentPage
                    items={selectActionItems}
                    paymentStatus={paymentStatus}
                    onPaymentComplete={handlePaymentComplete}
                    onBack={handleBackFromPayment}
                    onViewOrderSummary={
                      selectActionItems?.length > 0
                        ? () => handleViewOrderSummary(selectActionItems, orderSummaryOrderIdsByGroup)
                        : undefined
                    }
                  />
                </RestoreOrRedirect>
              }
            />
            <Route
              path="select-action"
              element={
                <RestoreOrRedirect
                  restoreKey="selectActionItems"
                  onRestore={(saved) => {
                    setSelectActionItems(saved.selectActionItems || []);
                  }}
                  hasData={selectActionItems.length > 0}
                  shopParam={shopParam}
                >
                  <LiffSelectActionPage
                    items={selectActionItems}
                    onBack={handleBackFromSelectAction}
                  />
                </RestoreOrRedirect>
              }
            />
            <Route
              path="success"
              element={
                <PaymentSuccessPage
                  accessToken={accessToken}
                  shopId={shopId}
                  onContinue={(items) => {
                    setSelectActionItems(items);
                    saveNavData({ selectActionItems: items });
                    navigate(`/liff/select-action${shopParam}`, { replace: true });
                  }}
                  onNeedMorePayment={() => {
                    const saved = loadNavData();
                    if (saved?.selectActionItems) setSelectActionItems(saved.selectActionItems);
                    if (saved?.paymentStatus) setPaymentStatus(saved.paymentStatus);
                    const q = shopParam ? `${shopParam}&after=group1` : '?after=group1';
                    navigate(`/liff/payment${q}`, { replace: true });
                  }}
                  onNeedGroup1Payment={() => {
                    const saved = loadNavData();
                    if (saved?.selectActionItems) setSelectActionItems(saved.selectActionItems);
                    if (saved?.paymentStatus) setPaymentStatus(saved.paymentStatus);
                    const q = shopParam ? `${shopParam}&after=group2` : '?after=group2';
                    navigate(`/liff/payment${q}`, { replace: true });
                  }}
                />
              }
            />
          </Routes>
        </LiffContext.Provider>
      )}
    </div>
  );
};

/**
 * Wrapper: if state data is missing (refresh/direct visit), try sessionStorage.
 * If still nothing, redirect to /liff.
 */
const RestoreOrRedirect = ({ restoreKey, onRestore, hasData, shopParam, children }) => {
  const navigate = useNavigate();
  const [resolved, setResolved] = useState(hasData);

  useEffect(() => {
    if (hasData) {
      setResolved(true);
      return;
    }
    const saved = loadNavData();
    if (saved && saved[restoreKey] != null) {
      onRestore(saved);
      setResolved(true);
    } else {
      navigate(`/liff${shopParam}`, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!resolved) return <LiffLoading />;
  return children;
};

/**
 * After credit card redirect from Beam: show success and let customer continue.
 * If group2 still needs payment → "ชำระยอดที่ 2" (after=group1).
 * If they paid group2 first, group1 may still need payment → "ชำระยอดที่ 1" (after=group2).
 */
const PaymentSuccessPage = ({ accessToken, shopId, onContinue, onNeedMorePayment, onNeedGroup1Payment }) => {
  const [loading, setLoading] = useState(true);
  const [needGroup2, setNeedGroup2] = useState(false);
  const [group2Total, setGroup2Total] = useState(0);
  const [needGroup1, setNeedGroup1] = useState(false);
  const [group1Total, setGroup1Total] = useState(0);

  useEffect(() => {
    if (!accessToken || !shopId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const nav = loadNavData() || {};
        const paidGroup = nav.ccPaymentGroup || null;
        if (paidGroup) {
          delete nav.ccPaymentGroup;
          saveNavData(nav);
        }

        const summary = await LiffService.getPaymentSummary(accessToken, shopId);
        if (cancelled) return;

        if (paidGroup === 'group2') {
          // Group2 CC was just paid — check if group1 still pending (they paid group2 first)
          if ((summary.group1Total ?? 0) > 0) {
            setNeedGroup1(true);
            setGroup1Total(summary.group1Total);
          }
          if (!cancelled) setLoading(false);
          return;
        }

        // Group1 CC was just paid (or unknown) — check if group2 remains
        if ((summary.group2Total ?? 0) > 0) {
          setNeedGroup2(true);
          setGroup2Total(summary.group2Total);
        }
      } catch (err) {
        console.error('[LIFF] payment-summary on success page failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [accessToken, shopId]);

  const handleContinue = async () => {
    const nav = loadNavData();
    if (nav?.selectActionItems && Array.isArray(nav.selectActionItems) && nav.selectActionItems.length > 0) {
      onContinue(nav.selectActionItems);
      return;
    }
    setLoading(true);
    try {
      const data = await LiffService.getAwaitingAction(accessToken, shopId);
      onContinue(data?.items || []);
    } catch (err) {
      console.error('[LIFF] getAwaitingAction after payment failed:', err);
      onContinue([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToGroup2 = () => {
    onNeedMorePayment();
  };

  const handleGoToGroup1 = () => {
    onNeedGroup1Payment?.();
  };

  if (loading) return <LiffLoading />;

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-gray-800 mb-2">ชำระเงินสำเร็จ</h2>
        <p className="text-gray-500 text-sm mb-4">ระบบรับทราบการชำระเงินของคุณแล้ว</p>

        {needGroup1 ? (
          <>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
              <p className="text-sm text-amber-800 font-medium">ชำระยอดที่ 2 สำเร็จ</p>
              <p className="text-sm text-amber-800 mt-1">
                ยังมียอดที่ 1 ค้างชำระ {Number.isFinite(group1Total) ? group1Total.toLocaleString('th-TH', { minimumFractionDigits: 2 }) : '0.00'} บาท
              </p>
              <p className="text-xs text-amber-700 mt-1">ค่าสินค้า / ค่าธรรมเนียม</p>
            </div>
            <button
              onClick={handleGoToGroup1}
              className="w-full bg-amber-400 hover:bg-amber-500 text-gray-900 py-3 rounded-xl font-semibold transition-colors"
            >
              ชำระยอดที่ 1 →
            </button>
          </>
        ) : needGroup2 ? (
          <>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
              <p className="text-sm text-amber-800 font-medium">
                ยังมียอดค้างชำระอีก {Number.isFinite(group2Total) ? group2Total.toLocaleString('th-TH', { minimumFractionDigits: 2 }) : '0.00'} บาท
              </p>
              <p className="text-xs text-amber-700 mt-1">ค่าน้ำหนัก / ค่าส่งในประเทศ</p>
            </div>
            <button
              onClick={handleGoToGroup2}
              className="w-full bg-amber-400 hover:bg-amber-500 text-gray-900 py-3 rounded-xl font-semibold transition-colors"
            >
              ชำระยอดที่ 2 →
            </button>
          </>
        ) : (
          <button
            onClick={handleContinue}
            className="w-full bg-green-500 text-white py-3 rounded-xl font-medium hover:bg-green-600 transition-colors"
          >
            เลือกวิธีรับสินค้า
          </button>
        )}
      </div>
    </div>
  );
};

export default LiffApp;
